import { snakeCaseObject } from '@edx/frontend-platform';

import { LETTER_REGEX, NUMBER_REGEX } from '../../data/constants';
import messages from '../messages';
import validateEmail from '../RegistrationFields/EmailField/validator';
import validateName from '../RegistrationFields/NameField/validator';
import validateUsername from '../RegistrationFields/UsernameField/validator';

/**
 * It validates the password field value
 * @param value
 * @param formatMessage
 * @returns {string}
 */
export const validatePasswordField = (value, formatMessage) => {
  let fieldError = '';
  if (!value || !LETTER_REGEX.test(value) || !NUMBER_REGEX.test(value) || value.length < 8) {
    fieldError = formatMessage(messages['password.validation.message']);
  }
  return fieldError;
};

/**
 * It accepts complete registration data as payload and checks if the form is valid.
 * @param payload
 * @param errors
 * @param configurableFormFields
 * @param fieldDescriptions
 * @param formatMessage
 * @returns {{fieldErrors, isValid: boolean}}
 */
export const isFormValid = (
  payload,
  errors,
  configurableFormFields,
  fieldDescriptions,
  formatMessage,
) => {
  const fieldErrors = { ...errors };
  let isValid = true;
  let emailSuggestion = { suggestion: '', type: '' };

  Object.keys(payload).forEach(key => {
    switch (key) {
    case 'name':
      if (!fieldErrors.name) {
        fieldErrors.name = validateName(payload.name, formatMessage);
      }
      if (fieldErrors.name) { isValid = false; }
      break;
    case 'email': {
      if (!fieldErrors.email) {
        const {
          fieldError, confirmEmailError, suggestion,
        } = validateEmail(payload.email, configurableFormFields?.confirm_email, formatMessage);
        if (fieldError) {
          fieldErrors.email = fieldError;
          isValid = false;
        }
        if (confirmEmailError) {
          fieldErrors.confirm_email = confirmEmailError;
          isValid = false;
        }
        emailSuggestion = suggestion;
      }
      if (fieldErrors.email) { isValid = false; }
      break;
    }
    case 'username':
      if (!fieldErrors.username) {
        fieldErrors.username = validateUsername(payload.username, formatMessage);
      }
      if (fieldErrors.username) { isValid = false; }
      break;
    case 'password':
      if (!fieldErrors.password) {
        fieldErrors.password = validatePasswordField(payload.password, formatMessage);
      }
      if (fieldErrors.password) { isValid = false; }
      break;
    default:
      break;
    }
  });

  // Don't validate when country field is optional or hidden and not present on registration form
  if (configurableFormFields?.country && !configurableFormFields.country?.displayValue) {
    fieldErrors.country = formatMessage(messages['empty.country.field.error']);
    isValid = false;
  } else if (configurableFormFields?.country && !configurableFormFields.country?.countryCode) {
    fieldErrors.country = formatMessage(messages['invalid.country.field.error']);
    isValid = false;
  }

  Object.keys(fieldDescriptions).forEach(key => {
    if (key === 'country' && !configurableFormFields?.country?.displayValue) {
      fieldErrors[key] = formatMessage(messages['empty.country.field.error']);
    } else if (!configurableFormFields[key]) {
      fieldErrors[key] = fieldDescriptions[key].error_message;
    }
    if (fieldErrors[key]) { isValid = false; }
  });

  // ATS custom field validation
  const roSelected = configurableFormFields?.country?.countryCode === 'RO';

  // Validate phone (always required)
  if (payload.phone === '' || payload.phone === undefined) {
    fieldErrors.phone = formatMessage(messages['empty.phone.field.error']);
    isValid = false;
  }

  // Validate occupation (always required)
  if (
    configurableFormFields?.occupation?.selectionId === ''
    || configurableFormFields.occupation === undefined
    || configurableFormFields.occupation === ''
  ) {
    fieldErrors.occupation = formatMessage(messages['empty.occupation.field.error']);
    isValid = false;
  } else {
    // Validate university/school_teach for teacher or student occupation
    if (
      configurableFormFields.occupation.selectionId === 'teacher'
      || configurableFormFields.occupation.selectionId === 'student'
    ) {
      if (roSelected) {
        if (configurableFormFields.occupation.selectionId === 'student') {
          if (
            configurableFormFields?.universityro?.selectionId === ''
            || configurableFormFields.universityro === undefined
            || configurableFormFields.universityro === ''
          ) {
            fieldErrors.universityro = formatMessage(messages['empty.university.field.list.error']);
            isValid = false;
          }
        }
        if (configurableFormFields.occupation.selectionId === 'teacher') {
          if (
            configurableFormFields?.school_teach?.selectionId === ''
            || configurableFormFields.school_teach === undefined
            || configurableFormFields.school_teach === ''
          ) {
            fieldErrors.school_teach = formatMessage(messages['empty.school_teach.field.list.error']);
            isValid = false;
          }
        }
      } else {
        if (configurableFormFields.occupation.selectionId === 'student') {
          if (
            configurableFormFields?.universityro === ''
            || configurableFormFields.universityro === undefined
          ) {
            fieldErrors.universityro = formatMessage(messages['empty.university.field.text.error']);
            isValid = false;
          }
        }
        if (configurableFormFields.occupation.selectionId === 'teacher') {
          if (
            configurableFormFields?.school_teach?.selectionId === ''
            || configurableFormFields.school_teach === undefined
            || configurableFormFields.school_teach === ''
          ) {
            fieldErrors.school_teach = formatMessage(messages['empty.school_teach.field.text.error']);
            isValid = false;
          }
        }
      }
    }

    // Validate high school for pupil occupation
    if (configurableFormFields.occupation.selectionId === 'pupil') {
      if (
        configurableFormFields?.highscoolro?.selectionId === ''
        || configurableFormFields.highscoolro === undefined
        || configurableFormFields.highscoolro === ''
      ) {
        const errorKey = roSelected
          ? 'empty.highschool.field.list.error'
          : 'empty.highschool.field.text.error';
        fieldErrors.highscoolro = formatMessage(messages[errorKey]);
        isValid = false;
      }
    }
  }

  // Validate region and town only if country is RO
  if (roSelected) {
    if (
      configurableFormFields?.region?.selectionId === ''
      || configurableFormFields.region === undefined
      || configurableFormFields.region === ''
    ) {
      fieldErrors.region = formatMessage(messages['empty.region.field.error']);
      isValid = false;
    }
    if (
      configurableFormFields?.town?.selectionId === ''
      || configurableFormFields.town === undefined
      || configurableFormFields.town === ''
    ) {
      fieldErrors.town = formatMessage(messages['empty.town.field.error']);
      isValid = false;
    }
  }

  return { isValid, fieldErrors, emailSuggestion };
};

/**
 * It prepares a payload for registration data that can be passed to registration API endpoint.
 * @param initPayload
 * @param configurableFormFields
 * @param showMarketingEmailOptInCheckbox
 * @param totalRegistrationTime
 * @param queryParams
 * @returns {*}
 */
export const prepareRegistrationPayload = (
  initPayload,
  configurableFormFields,
  showMarketingEmailOptInCheckbox,
  totalRegistrationTime,
  queryParams,
) => {
  let payload = { ...initPayload };
  Object.keys(configurableFormFields).forEach((fieldName) => {
    if (fieldName === 'country') {
      payload[fieldName] = configurableFormFields[fieldName].countryCode;
    } else if (
      ['occupation', 'highscoolro', 'region', 'town', 'universityro', 'school_teach'].includes(fieldName)
    ) {
      if (fieldName === 'occupation') {
        payload[fieldName] = configurableFormFields[fieldName].selectionId;
      } else if (payload.country === 'RO') {
        payload[fieldName] = configurableFormFields[fieldName].selectionValue
          ? configurableFormFields[fieldName].selectionValue
          : configurableFormFields[fieldName];
      } else {
        payload[fieldName] = configurableFormFields[fieldName];
      }
    } else {
      payload[fieldName] = configurableFormFields[fieldName];
    }
  });

  // Don't send the marketing email opt-in value if the flag is turned off
  if (!showMarketingEmailOptInCheckbox) {
    delete payload.marketingEmailsOptIn;
  }

  payload.totalRegistrationTime = totalRegistrationTime;
  payload = snakeCaseObject(payload);

  // Rename terms_of_service to newsletter for API compatibility
  const newsletter = payload.terms_of_service;
  delete payload.terms_of_service;
  payload.newsletter = newsletter;

  // add query params to the payload
  payload = { ...payload, ...queryParams };
  return payload;
};
